import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';

import Navigation from './Navigation';
import NavBar from './NavBar';
import Breadcrumb from './Breadcrumb';

import useWindowSize from '../../hooks/useWindowSize';
import useOutsideClick from '../../hooks/useOutsideClick';
import { ConfigContext } from '../../contexts/ConfigContext';
import * as actionType from '../../store/actions';
import { getCurrentSubscription, downgradeSubscription, changePlan } from '../../services/subscription';
import DowngradeModal from '../../views/subscription/DowngradeModal';
import UpgradeConfirmationModal from '../../views/subscription/UpgradeConfirmationModal';
import useAuth from '../../hooks/useAuth';

const AdminLayout = ({ children }) => {
  const windowSize = useWindowSize();
  const ref = useRef();
  const configContext = useContext(ConfigContext);
  const { updateProfile } = useAuth();

  const { collapseMenu, layout, subLayout, headerFixedLayout, configBlock } = configContext.state;
  const { dispatch } = configContext;

  // ── Global is_limit_pending gate ──────────────────────────────────────────
  const [limitPendingSub, setLimitPendingSub] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isProcessingUpgrade, setIsProcessingUpgrade] = useState(false);

  useEffect(() => {
    const checkLimitPending = async () => {
      try {
        const res = await getCurrentSubscription();
        // Support both response schema shapes:
        // Shape A: res.data.data  (subscription/current API)
        // Shape B: res.data.subscription  (dashboard / other APIs)
        const subData = res?.data?.data || res?.data?.subscription || null;
        if (subData && subData.is_limit_pending === true) {
          setLimitPendingSub(subData);
        } else {
          setLimitPendingSub(null);
        }
      } catch (err) {
        // Silently ignore — do not block layout on error
        console.error('AdminLayout: failed to check subscription limits', err);
      }
    };
    checkLimitPending();
  }, []);

  // Derive plan objects from the pending subscription for DowngradeModal
  // current_plan = the plan we downgraded TO (the new, lower plan)
  // previous_plan = the plan we were ON before
  const targetPlan = limitPendingSub?.current_plan || limitPendingSub?.plan || null;
  const previousPlan = limitPendingSub?.previous_plan || null;

  const handleLimitConfirm = async (data) => {
    try {
      await downgradeSubscription({
        plan_name: String(targetPlan?.name || targetPlan?.code || '').toUpperCase(),
        retained_auditors: data.retainedAuditors || [],
        retained_templates: data.retainedTemplates || [],
        retained_delegate_admins: data.retainedDelegateAdmins || []
      });
      // Sync updated subscription info into localStorage
      try {
        const userInfoStr = localStorage.getItem('userInfo');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          userInfo.subscription = targetPlan?.name || targetPlan?.code || userInfo.subscription;
          userInfo.is_limit_pending = false;
          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          updateProfile();
        }
      } catch (e) {
        console.error('AdminLayout: failed to sync userInfo after downgrade', e);
      }
      setLimitPendingSub(null);
    } catch (err) {
      console.error('AdminLayout: downgradeSubscription failed', err);
    }
  };

  const handleUpgradeConfirm = async () => {
    if (!previousPlan) return;
    setIsProcessingUpgrade(true);
    try {
      await changePlan(previousPlan.id);
      // Sync updated subscription info into localStorage
      try {
        const userInfoStr = localStorage.getItem('userInfo');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          userInfo.subscription = previousPlan?.name || previousPlan?.code || userInfo.subscription;
          userInfo.is_limit_pending = false;
          localStorage.setItem('userInfo', JSON.stringify(userInfo));
          updateProfile();
        }
      } catch (e) {
        console.error('AdminLayout: failed to sync userInfo after upgrade', e);
      }
      setLimitPendingSub(null);
      setShowUpgradeModal(false);
    } catch (err) {
      console.error('AdminLayout: upgrade failed', err);
    } finally {
      setIsProcessingUpgrade(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  useOutsideClick(ref, () => {
    if (collapseMenu) {
      dispatch({ type: actionType.COLLAPSE_MENU });
    }
  });

  useEffect(() => {
    if (windowSize.width > 992 && windowSize.width <= 1024 && layout !== 'horizontal') {
      dispatch({ type: actionType.COLLAPSE_MENU });
    }

    if (layout === 'horizontal' && windowSize.width < 992) {
      dispatch({ type: actionType.CHANGE_LAYOUT, layout: 'vertical' });
    }
  }, [dispatch, layout, windowSize]);

  const mobileOutClickHandler = () => {
    if (windowSize.width < 992 && collapseMenu) {
      dispatch({ type: actionType.COLLAPSE_MENU });
    }
  };

  let mainClass = ['pcoded-wrapper'];
  if (layout === 'horizontal' && subLayout === 'horizontal-2') {
    mainClass = [...mainClass, 'container'];
  }

  let common = (
    <React.Fragment>
      <Navigation />
      <NavBar />
    </React.Fragment>
  );

  let mainContainer = (
    <React.Fragment>
      <div className="pcoded-main-container">
        <div className={mainClass.join(' ')}>
          <div className="pcoded-content">
            <div className="pcoded-inner-content">
              <Breadcrumb />
              {children}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  const isMobile = windowSize.width < 992;
  let outSideClass = ['nav-outside'];
  if (isMobile) {
    if (collapseMenu) {
      outSideClass = [...outSideClass, 'mob-backdrop'];
    }
    if (headerFixedLayout) {
      outSideClass = [...outSideClass, 'mob-fixed'];
    }

    common = (
      <div className={outSideClass.join(' ')} ref={ref}>
        {common}
      </div>
    );
  }

  return (
    <React.Fragment>
      {common}
      {mainContainer}
      {isMobile && collapseMenu && (
        <div
          role="button"
          tabIndex="0"
          className="pcoded-outside"
          onClick={mobileOutClickHandler}
          onKeyDown={mobileOutClickHandler}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1030,
            cursor: 'pointer'
          }}
        />
      )}
      {configBlock}

      {/* Global compulsory downgrade modal — shown when is_limit_pending is true */}
      {limitPendingSub && !showUpgradeModal && (
        <DowngradeModal
          show={true}
          onHide={() => {}}
          targetPlan={targetPlan}
          currentPlan={previousPlan}
          currentPeriodEnd={limitPendingSub?.current_period_end || limitPendingSub?.trial_end}
          compulsory={true}
          onConfirm={handleLimitConfirm}
          onUpgradeToPreviousPlan={() => setShowUpgradeModal(true)}
        />
      )}

      {limitPendingSub && showUpgradeModal && (
        <UpgradeConfirmationModal
          show={true}
          onHide={() => setShowUpgradeModal(false)}
          targetPlan={previousPlan}
          currentPlan={targetPlan}
          subscription={limitPendingSub}
          onConfirm={handleUpgradeConfirm}
          isProcessing={isProcessingUpgrade}
        />
      )}
    </React.Fragment>
  );
};

AdminLayout.propTypes = {
  children: PropTypes.node
};

export default AdminLayout;
